/**
 * The Action Detail page's LLM Requests panel, grouped by the query that caused
 * each call.
 *
 * The MCP search servers fan out one path per query and record on the action row
 * which LLM_REQUESTS rows each path produced; the rows themselves carry no query,
 * and every row of an insert batch shares one CREATED_AT, so that id map is the
 * only thing that can attribute them. Actions that name no queries — core-agent
 * ones — come back as a single unnamed group and render as the flat list they
 * always were.
 */
import { useState, type JSX } from "react";
import type { LLMRequest } from "./types";
import { LLMRequestCard } from "./ActionDetailPanels";
import QueryFilterChips from "./QueryFilterChips";
import {
  parseMcpFanOut,
  groupMcpLlmRequestsByQuery,
  type McpLlmRequestGroup,
} from "./mcpAudit";

type RequestGroup = McpLlmRequestGroup<LLMRequest>;

interface ActionLlmRequestsProps {
  requests: LLMRequest[];
  /** The action row, read for its fan-out id map. */
  actionOutput: unknown;
  detailHref: (llmRequestId: string) => string;
}

interface FilterBarProps {
  groups: RequestGroup[];
  total: number;
  selected: number | null;
  onSelect: (position: number | null) => void;
}

function QueryFilterBar({ groups, total, selected, onSelect }: FilterBarProps): JSX.Element {
  return (
    <div className="mt-3">
      <QueryFilterChips
        chips={groups.map((group) => ({
          position: group.position,
          label: group.query ?? "No query recorded",
          count: group.requests.length,
        }))}
        total={total}
        selected={selected}
        onSelect={onSelect}
        ariaLabel="Filter LLM requests by query"
      />
    </div>
  );
}

function GroupHeading({ group }: { group: RequestGroup }): JSX.Element {
  return (
    <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {group.query === null ? "Not attributed to a query" : `Query ${group.position + 1}`}
      </p>
      {group.query !== null && <p className="text-sm text-gray-900">{group.query}</p>}
      <p className="text-xs text-gray-500">{group.requests.length} request(s)</p>
    </div>
  );
}

export default function ActionLlmRequests({
  requests,
  actionOutput,
  detailHref,
}: ActionLlmRequestsProps): JSX.Element {
  const [selected, setSelected] = useState<number | null>(null);
  const groups = groupMcpLlmRequestsByQuery(requests, parseMcpFanOut(actionOutput));
  const byQuery = groups.some((group) => group.query !== null);
  const visible = selected === null ? groups : groups.filter((g) => g.position === selected);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">LLM Requests ({requests.length})</h3>
        {byQuery && (
          <QueryFilterBar
            groups={groups}
            total={requests.length}
            selected={selected}
            onSelect={setSelected}
          />
        )}
      </div>

      {visible.map((group) => (
        <div key={group.position}>
          {byQuery && <GroupHeading group={group} />}
          <div className="divide-y divide-gray-200">
            {group.requests.map((request) => (
              // Numbered by its place in the whole action rather than within its
              // group, so filtering to one query does not renumber the rows it
              // leaves on screen. The groups hold the same objects as `requests`.
              <LLMRequestCard
                key={request.llm_request_id}
                request={request}
                index={requests.indexOf(request)}
                detailHref={detailHref(request.llm_request_id)}
              />
            ))}
          </div>
        </div>
      ))}

      {requests.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">No LLM requests found for this action.</div>
        </div>
      )}
    </div>
  );
}
